import React, { useCallback, useEffect } from 'react';
import { Box, Paper, useTheme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { ManageTeachingOrderOptions } from './ManageTeachingOrderOptions';
import { useModal } from '@lwtears/lwt-common-frontend/lib/Modal';
import { useAlert, AlertVariant, AlertStyleVariant } from 'state/providers/AlertProvider';
import { EditTeachingOrderModal } from 'components/@common/Modals/EditTeachingOrderModal';
import { BaseModalTitle } from 'components/@common/Modals/BaseModalTitle';
import { translate } from 'translations/translator';
import omit from 'lodash/omit';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import { AppStoreState, LESSON_PLANNER_VIEW } from 'types';
import { ManageSchoolStartDate } from './ManageSchoolStartDate';
import {
  updateStartDate,
  updateActiveTeachingOrder,
  getUserLessonPlans,
  renameTeachingOrder,
  deleteTeachingOrder,
  getProductConfiguration
} from 'state/modules/entity/thunks/teaching-order';
import { useNavigate, useParams, useLocation } from 'react-router';
import { TeachingOrderConfirmationModal } from 'components/@common/Modals/TeachingOrderConfirmationModal';
import { buildSearchQueryParams, parseQueryParams } from 'util/query-params-util';
import { DeleteTeachingOrderModal } from 'components/@common/Modals/DeleteTeachingOrderModal';
import {
  setLessonPlanWeek,
  incrementLessonPlanDay,
  incrementLessonPlanWeek,
  decramentLessonPlanDay,
  decramentLessonPlanWeek
} from 'state/modules/lessonPlanner/thunks';
import { ManageOptionsNav } from './ManageOptionsNav';
import { useManageOptionsProvider } from './ManageOptionsProvider';
import { ManageNonTeachingDays } from './ManageNonTeachingDays';
import { MANAGE_TEACHING_ORDER_COMPONENT } from './types';
import { ViewTeachingOrderModal } from 'components/@common/Modals/ViewTeachingOrderModal';
import LessonPlannerTranslationVariant from 'translations/variants/lessonPlanner';

const getTranslation = (key: LessonPlannerTranslationVariant) => translate('lessonPlanner', key);

export const ManageOptionsContainer: React.FC = () => {
  const [, { openModal }] = useModal();
  const [, { openAlert }] = useAlert();
  const navigate = useNavigate();
  const dispatch = useDispatch() as any;
  const theme = useTheme();
  const classes = useStyles(theme)();
  const { bookCode, edition } = useParams<{ bookCode: string; edition: string }>();
  const teachingOrders: any = useSelector(({ entity }: AppStoreState) => entity.teachingOrder);
  const lessonPlan: any = useSelector(({ entity }: AppStoreState) => entity.lessonPlan);
  const location = useLocation();
  const queryParams = parseQueryParams(location.search);
  const [, { setManageNavType }] = useManageOptionsProvider();

  const lessonDates = lessonPlan.lessons.reduce((acc, { lessonDate }) => {
    if (!lessonDate) return acc;
    acc.push(lessonDate);
    return acc;
  }, []);

  useEffect(() => {
    dispatch(getProductConfiguration());
    if (queryParams?.selected) {
      setManageNavType(queryParams?.selected);
    }
    // eslint-disable-next-line
  }, []);

  const [{ manageNavType }] = useManageOptionsProvider();

  const setActiveParam = active => {
    navigate({
      pathname: `/books/${bookCode}/${edition}/lesson-planner`,
      search: buildSearchQueryParams({ active })
    });
  };

  const openEditTeachingOrderModal = ({ id, custom }) => {
    openModal({
      body: (
        <EditTeachingOrderModal
          handleRenameTeachingOrder={name => handleRenameTeachingOrder({ id, custom, name })}
        />
      ),
      Title: () => (
        <BaseModalTitle
          text={getTranslation(LessonPlannerTranslationVariant.renameTeachingOrder)}
        />
      )
    });
  };

  const handleRenameTeachingOrder = async ({ id, custom, name }) => {
    await dispatch(renameTeachingOrder(id, custom, name));
    openAlert({
      body: getTranslation(LessonPlannerTranslationVariant.teachingOrderNameChangedSuccessfully),
      variant: AlertVariant.SUCCESS,
      styleVariant: AlertStyleVariant.FILLED
    });
  };

  const handleUseTeachingOrder = teachingOrder => async () => {
    await dispatch(updateActiveTeachingOrder(teachingOrder));
    setActiveParam(true);

    openAlert({
      body: getTranslation(LessonPlannerTranslationVariant.teachingOrderChangedSuccessfully),
      variant: AlertVariant.SUCCESS,
      styleVariant: AlertStyleVariant.FILLED
    });
  };

  const handleUseAction = async ({ id, custom }) => {
    const selectedTeachingOrder = teachingOrders.find(x => x.id === id && x.custom === custom);

    openModal({
      Title: () => (
        <BaseModalTitle
          text={getTranslation(LessonPlannerTranslationVariant.useThisTeachingOrder)}
        />
      ),
      body: (
        <TeachingOrderConfirmationModal
          handleUseTeachingOrder={handleUseTeachingOrder(selectedTeachingOrder)}
          teachingOrderTitle={selectedTeachingOrder.name}
          teachingOrderDocumentation={selectedTeachingOrder.documentationUrl}
        />
      )
    });
  };

  const { view: selectedView } = useSelector(
    ({ lessonPlanner }: AppStoreState) => lessonPlanner,
    shallowEqual
  );

  const handleNextClick = () => {
    if (selectedView === LESSON_PLANNER_VIEW.DAY) {
      dispatch(incrementLessonPlanDay());
      return;
    }
    dispatch(incrementLessonPlanWeek());
  };

  const handlePreviousClick = () => {
    if (selectedView === LESSON_PLANNER_VIEW.DAY) {
      dispatch(decramentLessonPlanDay());
      return;
    }
    dispatch(decramentLessonPlanWeek());
  };

  const handleViewAction = async ({ id, custom }) => {
    const currentTeachingOrder = teachingOrders.find(x => x.id === id && x.custom === custom);
    if (currentTeachingOrder?.id) {
      const { data }: any = await dispatch(getUserLessonPlans(currentTeachingOrder));

      await openModal({
        Title: () => <BaseModalTitle text={currentTeachingOrder.name} />,
        body: (
          <ViewTeachingOrderModal
            handleNextClick={handleNextClick}
            handlePreviousClick={handlePreviousClick}
            lessonPlan={data.lessonPlan}
          />
        )
      });
    }
  };

  const handleDeleteTeachingOrder = selectedTeachingOrder => async () => {
    await dispatch(deleteTeachingOrder(selectedTeachingOrder));

    openAlert({
      body: getTranslation(LessonPlannerTranslationVariant.teachingOrderChangedSuccessfully),
      variant: AlertVariant.SUCCESS,
      styleVariant: AlertStyleVariant.FILLED
    });
  };

  const handleDeleteAction = async ({ id, custom }) => {
    const selectedTeachingOrder = teachingOrders.find(x => x.id === id && x.custom === custom);
    if (selectedTeachingOrder?.id) {
      openModal({
        Title: () => (
          <BaseModalTitle
            text={getTranslation(LessonPlannerTranslationVariant.deleteTeachingOrderTitle)}
          />
        ),
        body: (
          <DeleteTeachingOrderModal
            handleDeleteTeachingOrder={handleDeleteTeachingOrder(selectedTeachingOrder)}
            teachingOrderTitle={selectedTeachingOrder.name}
          />
        )
      });
    }
  };

  const getTeachingOrderActions = custom => {
    const teachingOrderActions = {
      onViewAction: handleViewAction,
      onUseAction: handleUseAction,
      onRenameAction: openEditTeachingOrderModal,
      onDeleteAction: handleDeleteAction
    };

    if (custom) return teachingOrderActions;

    return omit(teachingOrderActions, ['onRenameAction', 'onDeleteAction']);
  };

  const handleSave = useCallback(
    async date => {
      await dispatch(updateStartDate(date));
      dispatch(setLessonPlanWeek(1));
      openAlert({
        body: getTranslation(LessonPlannerTranslationVariant.startDateUpdatedSuccessfully),
        variant: AlertVariant.SUCCESS
      });
    },
    [dispatch, openAlert]
  );

  const componentHandlers = {
    handleSave,
    startDate: new Date(lessonDates[0]),
    getTeachingOrderActions,
    teachingOrders
  };

  return (
    <Box className={classes.containerWrapper}>
      <Paper className={classes.paperContainer}>
        <ManageOptionsNav />
        <ManageViewSwitch manageNavType={manageNavType} componentHandlers={componentHandlers} />
      </Paper>
    </Box>
  );
};

const ManageViewSwitch = ({ manageNavType, componentHandlers }) => {
  switch (manageNavType) {
    case MANAGE_TEACHING_ORDER_COMPONENT.EditStartDate:
      return (
        <ManageSchoolStartDate
          handleSave={componentHandlers.handleSave}
          startDate={componentHandlers.startDate}
        />
      );
    case MANAGE_TEACHING_ORDER_COMPONENT.ManageNonTeachingDays:
      return <ManageNonTeachingDays />;

    case MANAGE_TEACHING_ORDER_COMPONENT.ManageTeachingOrders:
      return (
        <ManageTeachingOrderOptions
          getTeachingOrderActions={componentHandlers.getTeachingOrderActions}
          teachingOrders={componentHandlers.teachingOrders}
        />
      );

    default:
      return null;
  }
};

const useStyles = theme =>
  makeStyles(() => ({
    containerWrapper: {
      display: 'flex',
      flex: 1,
      alignItems: 'center',
      flexDirection: 'column',
      height: '95vh',
      backgroundColor: theme.palette.primary.light,
      minHeight: '100%'
    },
    paperContainer: {
      display: 'flex',
      flexDirection: 'column',
      marginTop: 22,
      padding: 24,
      maxWidth: 1256,
      width: '85vw'
    },
    backBtn: {
      width: 230,
      '&:hover': {
        backgroundColor: theme.palette.primary.light
      }
    }
  }));
